/**
 * Export Agent Review data — review results or automated Ace profile comparisons.
 */

import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import { SegmentedTabs } from "@/components/ui/segmented-tabs/SegmentedTabs";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import {
  agentReviewAPI,
  type AceComparisonExportRequest,
  type AceProfileInfo,
  type ExportResultsOptions,
  type RunListItem,
} from "@/services/api/agentReview";
import type { AceProfile } from "./types";

export type ExportDialogTab = "results" | "ace_comparisons" | "calibration";

export interface ExportResultsDialogProps {
  open: boolean;
  taskTypeLabel: string;
  reviewedCount: number;
  reviewedCases: RunListItem[];
  showLoaOption: boolean;
  showCalibrationTab?: boolean;
  calibrationDisputeCount?: number;
  exporting: boolean;
  error: string | null;
  onClose: () => void;
  onExportResults: (options: ExportResultsOptions) => void;
  onExportAceComparisons: (request: AceComparisonExportRequest) => void;
  onExportCalibration?: () => void;
}

const DEFAULT_RESULTS_OPTIONS: ExportResultsOptions = {
  include_summary: true,
  include_reviews: true,
  include_checks: true,
  include_loa: true,
};

const ALL_PROFILES: AceProfile[] = ["strict", "balanced", "lenient"];

const FALLBACK_PROFILES: AceProfileInfo[] = [
  { id: "strict", label: "Strict", description: "Original Ace auditor", is_default: true },
  { id: "balanced", label: "Balanced", description: "Moderate strictness", is_default: false },
  { id: "lenient", label: "Lenient", description: "Benefit of the doubt", is_default: false },
];

function caseLabel(item: RunListItem): string {
  return item.subject_label?.trim() || item.label?.trim() || item.run_id.slice(-8);
}

function hasResultsSelection(options: ExportResultsOptions): boolean {
  return Object.values(options).some(Boolean);
}

export function ExportResultsDialog({
  open,
  taskTypeLabel,
  reviewedCount,
  reviewedCases,
  showLoaOption,
  showCalibrationTab = false,
  calibrationDisputeCount = 0,
  exporting,
  error,
  onClose,
  onExportResults,
  onExportAceComparisons,
  onExportCalibration,
}: ExportResultsDialogProps) {
  const [tab, setTab] = useState<ExportDialogTab>("results");
  const [resultsOptions, setResultsOptions] = useState<ExportResultsOptions>(DEFAULT_RESULTS_OPTIONS);
  const [aceProfiles, setAceProfiles] = useState<AceProfileInfo[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState<Set<AceProfile>>(() => new Set(ALL_PROFILES));
  const [selectedRunIds, setSelectedRunIds] = useState<Set<string>>(() => new Set());
  const [forceRerun, setForceRerun] = useState(false);
  const [includeTrialChecks, setIncludeTrialChecks] = useState(true);

  useEffect(() => {
    if (!open) return;
    setTab("results");
    setResultsOptions(DEFAULT_RESULTS_OPTIONS);
    setSelectedProfiles(new Set(ALL_PROFILES));
    setSelectedRunIds(new Set(reviewedCases.map((item) => item.run_id)));
    setForceRerun(false);
    setIncludeTrialChecks(true);
  }, [open, reviewedCases]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setProfilesLoading(true);
    void agentReviewAPI
      .listAceProfiles()
      .then((rows) => {
        if (!cancelled) setAceProfiles(rows);
      })
      .catch(() => {
        if (!cancelled) setAceProfiles(FALLBACK_PROFILES);
      })
      .finally(() => {
        if (!cancelled) setProfilesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const profileRows = aceProfiles.length > 0 ? aceProfiles : FALLBACK_PROFILES;
  const aceRunEstimate = selectedRunIds.size * selectedProfiles.size;
  const isBackgroundAceExport = tab === "ace_comparisons";

  const resultsSections = useMemo(
    () =>
      [
        {
          key: "include_summary" as const,
          label: "Summary",
          description: "Cohort stats and RAG distribution overview.",
        },
        {
          key: "include_reviews" as const,
          label: "Reviews",
          description: "One row per case — human vs primary Ace RAGs and notes.",
        },
        {
          key: "include_checks" as const,
          label: "Checks",
          description: "Per-check human and primary Ace answers with match flags.",
        },
        {
          key: "include_loa" as const,
          label: "LOA reviews & checks",
          description: "Letter-of-authority sheets when this task type includes LOAs.",
          hidden: !showLoaOption,
        },
      ].filter((section) => !section.hidden),
    [showLoaOption],
  );

  const toggleResultsOption = (key: keyof ExportResultsOptions) => {
    setResultsOptions((current) => ({ ...current, [key]: !current[key] }));
  };

  const toggleProfile = (profile: AceProfile) => {
    setSelectedProfiles((current) => {
      const next = new Set(current);
      if (next.has(profile)) next.delete(profile);
      else next.add(profile);
      return next;
    });
  };

  const toggleRun = (runId: string) => {
    setSelectedRunIds((current) => {
      const next = new Set(current);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  };

  const selectAllRuns = () => setSelectedRunIds(new Set(reviewedCases.map((item) => item.run_id)));
  const clearRuns = () => setSelectedRunIds(new Set());

  const handleConfirm = () => {
    if (tab === "results") {
      onExportResults(resultsOptions);
      return;
    }
    if (tab === "calibration") {
      onExportCalibration?.();
      return;
    }
    onExportAceComparisons({
      runIds: [...selectedRunIds],
      aceProfiles: [...selectedProfiles],
      force: forceRerun,
      includeTrialChecks,
    });
  };

  const canConfirm =
    tab === "results"
      ? hasResultsSelection(resultsOptions)
      : tab === "calibration"
        ? Boolean(onExportCalibration)
        : selectedProfiles.size > 0 && selectedRunIds.size > 0;

  return (
    <Dialog open={open} onClose={onClose} data-testid="export-results-dialog">
      <DialogTitle>Export</DialogTitle>
      <DialogBody className="space-y-4">
        <p className="text-sm text-slate-300 leading-relaxed">
          <span className="text-slate-100">{taskTypeLabel}</span>
          {reviewedCount > 0 ? (
            <>
              {" "}
              — <span className="tabular-nums">{reviewedCount}</span> reviewed case
              {reviewedCount === 1 ? "" : "s"}
            </>
          ) : null}
        </p>

        <SegmentedTabs
          activeId={tab}
          onChange={(id) => setTab(id as ExportDialogTab)}
          tabs={[
            { id: "results", label: "Review results" },
            { id: "ace_comparisons", label: "Ace comparisons" },
            ...(showCalibrationTab ? [{ id: "calibration", label: "Calibration" }] : []),
          ]}
          tabTestIdPrefix="export-tab"
        />

        {tab === "results" ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">
              Download human review data and the primary Ace rating already on each case.
            </p>
            {resultsSections.map((section) => {
              const checked = resultsOptions[section.key];
              return (
                <label
                  key={section.key}
                  className={clsx(
                    "flex cursor-pointer gap-3 rounded-lg px-3 py-3 ring-1 transition-colors",
                    checked
                      ? "bg-indigo-500/[0.08] ring-indigo-500/25"
                      : "bg-white/[0.02] ring-white/10 hover:bg-white/[0.04]",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleResultsOption(section.key)}
                    className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-900 text-indigo-500 focus:ring-indigo-500/40"
                  />
                  <span className="min-w-0 space-y-0.5">
                    <span className="block text-sm font-medium text-slate-200">{section.label}</span>
                    <span className="block text-xs text-slate-500 leading-relaxed">
                      {section.description}
                    </span>
                  </span>
                </label>
              );
            })}
            {!hasResultsSelection(resultsOptions) ? (
              <p className="text-sm text-amber-300/90">Select at least one section to export.</p>
            ) : null}
          </div>
        ) : tab === "calibration" ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500 leading-relaxed">
              Download every human vs Ace check disagreement in this session, including pass/fail/N/A
              answers, human comments, Ace reasons, and any resolution you recorded.
            </p>
            <div className="rounded-lg bg-white/[0.03] px-3 py-3 ring-1 ring-white/10 text-sm text-slate-300 space-y-1">
              <p>
                <span className="text-slate-500">Disputes in scope:</span>{" "}
                <span className="tabular-nums">{calibrationDisputeCount}</span>
              </p>
              <p className="text-xs text-slate-500">
                Sheets: Summary counts and a Disputes row per mismatched check.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-slate-500 leading-relaxed">
              Ace runs each selected profile on the chosen cases automatically, then downloads a
              side-by-side comparison of phase RAGs and check answers across profiles.
            </p>

            <div className="space-y-2">
              <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                Grading profiles to run
              </p>
              {profilesLoading ? (
                <LoadingPlaceholder text="Loading profiles…" />
              ) : (
                profileRows.map((profile) => (
                  <label
                    key={profile.id}
                    className={clsx(
                      "flex cursor-pointer gap-3 rounded-lg px-3 py-3 ring-1 transition-colors",
                      selectedProfiles.has(profile.id)
                        ? "bg-indigo-500/[0.08] ring-indigo-500/25"
                        : "bg-white/[0.02] ring-white/10 hover:bg-white/[0.04]",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={selectedProfiles.has(profile.id)}
                      onChange={() => toggleProfile(profile.id)}
                      className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-900 text-indigo-500 focus:ring-indigo-500/40"
                    />
                    <span className="min-w-0 space-y-0.5">
                      <span className="block text-sm font-medium text-slate-200">
                        {profile.label}
                        {profile.is_default ? " (original)" : ""}
                      </span>
                      <span className="block text-xs text-slate-500 leading-relaxed">
                        {profile.description}
                      </span>
                    </span>
                  </label>
                ))
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                  Cases to compare
                </p>
                <div className="flex gap-2 text-[11px]">
                  <button
                    type="button"
                    onClick={selectAllRuns}
                    className="text-indigo-300 hover:text-indigo-200"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={clearRuns}
                    className="text-slate-500 hover:text-slate-300"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1 rounded-lg ring-1 ring-white/10 p-2">
                {reviewedCases.length === 0 ? (
                  <p className="text-xs text-slate-500 px-2 py-1">No reviewed cases in this session.</p>
                ) : (
                  reviewedCases.map((item) => (
                    <label
                      key={item.run_id}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-white/[0.03]"
                    >
                      <input
                        type="checkbox"
                        checked={selectedRunIds.has(item.run_id)}
                        onChange={() => toggleRun(item.run_id)}
                        className="h-3.5 w-3.5 rounded border-white/20 bg-slate-900 text-indigo-500"
                      />
                      <span className="text-sm text-slate-200 truncate">{caseLabel(item)}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <label className="flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2 ring-1 ring-white/10">
              <input
                type="checkbox"
                checked={includeTrialChecks}
                onChange={() => setIncludeTrialChecks((value) => !value)}
                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-900 text-indigo-500"
              />
              <span className="text-sm text-slate-300">Include Ace reasoning columns per profile</span>
            </label>

            <label className="flex cursor-pointer items-start gap-3 rounded-lg px-3 py-2 ring-1 ring-white/10">
              <input
                type="checkbox"
                checked={forceRerun}
                onChange={() => setForceRerun((value) => !value)}
                className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-900 text-indigo-500"
              />
              <span className="text-sm text-slate-300">
                Re-run Ace even when a trial already exists for that profile
              </span>
            </label>

            {aceRunEstimate > 0 ? (
              <p className="text-xs text-amber-300/90">
                Up to {aceRunEstimate} Ace run{aceRunEstimate === 1 ? "" : "s"} — queued in the
                background; progress shows on the Agent Review screen after you start.
              </p>
            ) : null}
            {selectedProfiles.size === 0 || selectedRunIds.size === 0 ? (
              <p className="text-sm text-amber-300/90">
                Select at least one profile and one case to export comparisons.
              </p>
            ) : null}
          </div>
        )}

        {error ? <p className="text-sm text-rose-400">{error}</p> : null}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={(exporting && tab === "results") || (exporting && tab === "calibration") || !canConfirm}
          data-testid="export-results-confirm"
        >
          {exporting
            ? "Exporting…"
            : isBackgroundAceExport
              ? "Run in background"
              : "Download Excel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
